// Users (admin) — registration IS creation here (invite-only): only emails in
// this list can complete Google sign-in. Curro redesign: role-chooser cards,
// tenant scope picked with source/campus toggle-chips, users table collapses
// to cards on mobile. Tenant users carry a scope (sources and/or campuses)
// that the server force-applies to every query they make.
// Data wiring (UsersApi) is unchanged from the previous version.

import { useEffect, useState } from "react";
import { UsersApi, AnalyticsApi, PipelineApi } from "../hooks/useApi.js";
import CustomerSelect from "../components/CustomerSelect.jsx";
import Icon from "../components/Icon.jsx";

const ROLES = [
  { id: "admin",  icon: "shield",   color: "var(--curro-blue)",   desc: "Full access + diagnostics" },
  { id: "client", icon: "globe",    color: "var(--curro-violet)", desc: "Whole network, read-only" },
  { id: "tenant", icon: "building", color: "var(--curro-green)",  desc: "Scoped to their customer(s)" },
];
const roleColor = Object.fromEntries(ROLES.map((r) => [r.id, r.color]));

const EMPTY = { email: "", displayName: "", role: "client", sourceKeys: [], entityIds: [], canViewAiSummary: false, password: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);   // null = create; else editing this user id
  const [campuses, setCampuses] = useState([]);
  const [custRevenue, setCustRevenue] = useState({});
  const [sources, setSources] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = () => UsersApi.list().then(setUsers).catch((e) => setError(e.message));
  useEffect(() => {
    load();
    AnalyticsApi.meta().then((m) => setCampuses(m.organisations ?? [])).catch(() => {});
    // Scope picker options are organisation NAMES, so key revenue by the display name (label).
    AnalyticsApi.breakdown("campus", {}).then((rows) => setCustRevenue(Object.fromEntries(rows.map((r) => [r.label ?? r.key, r.spend])))).catch(() => {});
    PipelineApi.sources().then((s) => setSources(s.map((x) => x.sourceKey))).catch(() => {});
  }, []);

  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const scopeOf = (f) =>
    f.role === "tenant" ? { sourceKeys: f.sourceKeys, entityIds: f.entityIds } : null;

  // Populate the form from an existing user and switch the panel into edit mode.
  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      email: u.email,
      displayName: u.displayName ?? "",
      role: u.role,
      sourceKeys: u.scope?.sourceKeys ?? [],
      entityIds: u.scope?.entityIds ?? [],
      canViewAiSummary: u.canViewAiSummary ?? false,
      password: "",
    });
    setError(null); setNotice(null);
    setOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Create (editingId null) or update (editingId set) — one form for both.
  const save = async () => {
    setError(null); setNotice(null);
    const pw = (form.password ?? "").trim();
    if (pw && pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    const payload = { email: form.email, displayName: form.displayName || null, role: form.role, scope: scopeOf(form),
      canViewAiSummary: form.role === "tenant" ? form.canViewAiSummary : false };
    try {
      let id = editingId;
      if (editingId) {
        const cur = users.find((u) => u.id === editingId);
        await UsersApi.update(editingId, { ...payload, status: cur?.status ?? "active" });
      } else {
        const created = await UsersApi.create(payload);
        id = created.id;
      }
      if (pw) await UsersApi.setPassword(id, pw);   // email+password login for non-Google users
      setNotice(`${form.email} ${editingId ? "updated" : `registered as ${form.role}`}${pw ? " · password set" : ""}.`);
      setForm(EMPTY); setEditingId(null); setOpen(false); load();
    } catch (e) { setError(e.body?.error ?? e.message); }
  };

  const toggleStatus = async (u) => {
    setError(null);
    try {
      await UsersApi.update(u.id, { email: u.email, role: u.role, scope: u.scope, status: u.status === "active" ? "disabled" : "active", canViewAiSummary: u.canViewAiSummary });
      load();
    } catch (e) { setError(e.body?.error ?? e.message); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They will no longer be able to sign in.`)) return;
    try { await UsersApi.remove(u.id); load(); } catch (e) { setError(e.body?.error ?? e.message); }
  };

  const fmtLogin = (s) => (s ? s.slice(0, 16).replace("T", " · ") : "never");
  const scopeText = (u) => {
    if (u.role === "admin") return "Everything";
    if (u.role === "client") return "Whole network";
    const sk = u.scope?.sourceKeys ?? [], ei = u.scope?.entityIds ?? [];
    if (sk.length === 0 && ei.length === 0) return "All data (full access)";   // unscoped tenant
    return [sk.join(", "), ei.join(", ")].filter(Boolean).join(" · ");
  };

  return (
    <div className="rise">
      <div className="page-head">
        <div>
          <div className="eyebrow">Admin · Access</div>
          <h1>Users &amp; access</h1>
          <p className="lead">Invite-only: only registered, active emails can sign in. Admins see everything, clients see the
            whole network read-only, and tenant users are auto-scoped server-side to their own slice.</p>
        </div>
        <button type="button" className={`btn ${open ? "ghost" : "primary"} lg`}
          onClick={() => { setForm(EMPTY); setEditingId(null); setError(null); setNotice(null); setOpen(!open); }}>
          <Icon name={open ? "x" : "plus"} size={17} /> {open ? "Cancel" : "Invite user"}
        </button>
      </div>

      {open && (
        <div className="panel rise" style={{ borderTop: "3px solid var(--primary)" }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}><Icon name={editingId ? "settings" : "plus"} size={13} /> {editingId ? "Edit user" : "Register a user"}</div>
          <div className="controls">
            <div className="field"><label>Email {editingId && <span className="hint">(cannot be changed)</span>}</label>
              <input type="email" value={form.email} placeholder="name@company.com" readOnly={!!editingId}
                style={editingId ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
                onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="field"><label>Display name</label>
              <input type="text" value={form.displayName} placeholder="optional" onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></div>
          </div>

          <div className="field" style={{ marginTop: 14 }}><label>Role</label>
            <div className="choice-grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
              {ROLES.map((r) => (
                <button type="button" key={r.id} className={`choice ${form.role === r.id ? "on" : ""}`} onClick={() => setForm({ ...form, role: r.id })}>
                  <div className="check"><Icon name="check" size={12} /></div>
                  <div className="ci" style={{ color: r.color }}><Icon name={r.icon} size={18} /></div>
                  <b style={{ textTransform: "capitalize" }}>{r.id}</b><small>{r.desc}</small>
                </button>
              ))}
            </div>
          </div>

          {form.role === "tenant" && (
            <div className="rise" style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
              {form.sourceKeys.length === 0 && form.entityIds.length === 0 && (
                <div className="notice" style={{ margin: 0 }}>
                  <Icon name="globe" size={14} /> No scope selected — this tenant will have <b>full access to all data</b> (like the whole network). Add sources/customers below to restrict them.
                </div>
              )}
              <div className="fb-section">
                <div className="fb-label">Scope to sources <span className="fb-count">{form.sourceKeys.length || "all"}</span></div>
                <div className="chipset">
                  {sources.map((s) => {
                    const on = form.sourceKeys.includes(s);
                    return <button type="button" key={s} className={`tchip ${on ? "on" : ""}`} onClick={() => toggle("sourceKeys", s)}>{on && <Icon name="check" size={13} />}{s}</button>;
                  })}
                </div>
              </div>
              <div className="fb-section">
                <div className="fb-label">Scope to customers <span className="fb-count">{form.entityIds.length || "all"}</span></div>
                <CustomerSelect
                  value={form.entityIds}
                  onChange={(entityIds) => setForm((f) => ({ ...f, entityIds }))}
                  options={campuses}
                  revenueByKey={custRevenue}
                />
                <p className="hint" style={{ marginTop: 4 }}>Leave empty to allow all customers in their assigned source(s). Enforced on every query server-side.</p>
              </div>
              <div className="fb-section">
                <div className="fb-label">Permissions</div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.canViewAiSummary}
                    onChange={(e) => setForm((f) => ({ ...f, canViewAiSummary: e.target.checked }))} />
                  <span>Can view the AI executive summary</span>
                </label>
                <p className="hint" style={{ marginTop: 4 }}>Off by default. The AI summary is portfolio-wide, so only grant this to tenants allowed to see company-level insights.</p>
              </div>
            </div>
          )}

          <div className="field" style={{ marginTop: 14 }}>
            <label>Password <span className="hint">(optional — for users without a Google account)</span></label>
            <input type="password" value={form.password} autoComplete="new-password"
              placeholder={editingId ? "Leave blank to keep unchanged" : "Set a password, or leave blank for Google-only"}
              onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <p className="hint" style={{ marginTop: 4 }}>Min 8 characters. If set, the user can sign in with their email + this password (as well as Google). Share it with them securely.</p>
          </div>

          <div style={{ marginTop: 16 }}>
            <button type="button" className="btn primary" disabled={!form.email} onClick={save}>
              <Icon name="check" size={15} /> {editingId ? "Save changes" : `Register ${form.role}`}
            </button>
          </div>
        </div>
      )}

      {error && <div className="error"><Icon name="alert" size={16} /> {error}</div>}
      {notice && <div className="notice"><Icon name="check" size={16} /> {notice}</div>}

      <h2 className="section-title">Registered users <span className="count">{users.length}</span></h2>
      <div className="tbl-wrap m-cards-wrap">
        <table className="m-cards">
          <thead><tr><th>Person</th><th>Role</th><th>Scope</th><th>Status</th><th>Last login</th><th>Actions</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                    <span style={{ width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", color: "#fff", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13, flex: "none", background: roleColor[u.role] ?? "var(--muted-2)" }}>
                      {(u.displayName || u.email)[0].toUpperCase()}
                    </span>
                    <div style={{ lineHeight: 1.25 }}>
                      <b style={{ color: "var(--ink)", display: "block" }}>{u.displayName ?? "—"}</b>
                      <span className="num" style={{ fontSize: 11.5, color: "var(--muted)" }}>{u.email}</span>
                    </div>
                  </div>
                </td>
                <td data-label="Role"><span className={`tag role-${u.role}`}>{u.role}</span></td>
                <td className="num hint" data-label="Scope">{scopeText(u)}</td>
                <td data-label="Status">
                  {u.status === "active"
                    ? <span className="chip ok"><Icon name="check" size={12} /> Active</span>
                    : <span className="chip muted"><Icon name="x" size={12} /> Disabled</span>}
                </td>
                <td className="num hint" data-label="Last login">{fmtLogin(u.lastLoginAt)}</td>
                <td data-label="Actions">
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button type="button" className="btn ghost sm" onClick={() => startEdit(u)}>Edit</button>
                    <button type="button" className="btn ghost sm" onClick={() => toggleStatus(u)}>{u.status === "active" ? "Disable" : "Enable"}</button>
                    <button type="button" className="btn danger sm" onClick={() => remove(u)} aria-label={`Remove ${u.email}`}><Icon name="trash" size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
